"use client";

import type {
  TourPageWhatsappContactView,
  TourPageWhatsappView,
} from "@tripcart/shared";

import { useFormatMoney } from "@/lib/currency-format-context";
import {
  localDateStr,
  tourPageDepartureChatUrl,
  tourPageSurfaceProps,
} from "@tripcart/shared";

import { WhatsappMark } from "@/components/whatsapp-mark";
import type { Translator } from "@/lib/translations";

import styles from "./departure-details.module.css";

/** The facts a careful buyer checks before paying, derived from the departure
 *  so they can never contradict the booking rail.
 *
 *  Every row is CONDITIONAL. An operator who has set no payment policy gets a
 *  short panel — not a panel full of plausible defaults. This is the surface
 *  where an invented "2 to 12 travelers" or a guessed "Free cancellation
 *  until…" would do real damage: someone reads it, plans around it, and we
 *  have told them something we made up. Sparse and true beats complete and
 *  fabricated.
 *
 *  Everything else about a departure — meeting point, guide languages, and
 *  any other detail — is configured operator-side via Tours → Configuration
 *  → Tour Page (`DepartureExtras.lines` below), not derived here.
 *
 *  The guide's own panel also carries a cancellation deadline and a fixed
 *  "Permits are filed before you fly" footer. Neither is here: we hold no
 *  cancellation-policy field at all, and the footer is Manaslu-specific copy. */
export interface DepartureFacts {
  depositPercent: number | null;
  balanceDueDaysBefore: number | null;
}

/** What the operator added in Tours → Configuration → Tour Page: their own
 *  lines, and the two WhatsApp surfaces with their own number and greeting.
 *
 *  Icons arrive as resolved SVG paths rather than names, because this is a
 *  client component and the icon registry must stay on the server.
 *
 *  The two WhatsApp keys are ABSENT unless that exact surface should render —
 *  `buildTourPageView` decides, so this component never re-derives "is it
 *  switched on, and is there a number". They carry the message with its
 *  `{tour_name}` / `{date}` placeholders still in it: the date differs on every
 *  row, so the link is built per row from `subject` below. */
export interface DepartureExtras {
  lines: { text: string; iconPath: string | null }[];
  /** The icon button beside Book now. */
  whatsappButton?: TourPageWhatsappView | undefined;
  /** The "Ask on WhatsApp" line at the foot of this panel. */
  whatsappContact?: TourPageWhatsappContactView | undefined;
  /** What `{tour_name}` and `{tour_url}` resolve to. The date is added per row. */
  subject: { name: string; url: string };
}

/** The `wa.me` link for ONE departure — the shared chat plus this row's date.
 *
 *  The date is already formatted in the agency's locale by the caller, because
 *  `@tripcart/shared` holds no locale and a traveller reading "2026-10-14" in a
 *  WhatsApp draft would not recognise their own trip. */
export function departureChatUrl(
  chat: TourPageWhatsappView,
  extras: DepartureExtras,
  formattedDate: string,
): string {
  return tourPageDepartureChatUrl(chat, {
    name: extras.subject.name,
    url: extras.subject.url,
    date: formattedDate,
  });
}

/** A detail's glyph. The operator can clear the icon, and a name can fall out
 *  of the registry, so a missing path draws nothing rather than a placeholder
 *  that would imply a meaning it does not have. */
function DetailGlyph({ path }: { path: string | null }) {
  if (!path) return <span aria-hidden className={styles.glyph} />;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={styles.glyph}
    >
      {path.split("|").map((d, index) => (
        <path key={index} d={d.trim()} />
      ))}
    </svg>
  );
}

export function DepartureDetails({
  priceAmount,
  startIso,
  facts,
  extras,
  formatDate,
  t,
}: {
  /** The row's per-traveller price, in minor units. */
  priceAmount: number;
  startIso: string;
  facts: DepartureFacts;
  /** The operator's own lines and WhatsApp surfaces. Absent when they have not
   *  set any up, which leaves the panel exactly as it was. */
  extras?: DepartureExtras | undefined;
  formatDate: (iso: string) => string;
  t: Translator;
}) {
  const formatMoney = useFormatMoney();
  const rows = buildFactRows({
    priceAmount,
    startIso,
    facts,
    formatMoney,
    formatDate,
    t,
  });

  const lines = extras?.lines ?? [];
  const contact = extras?.whatsappContact;

  // Nothing to show at all — not "no facts". The panel has THREE possible
  // contents and needs only one of them.
  //
  // It has been widened twice for the same reason. First it hid whenever the
  // tour carried no derived facts, which erased an operator's own detail
  // lines. Then it still hid when the only thing configured was the WhatsApp
  // contact link: an operator on a minimal tour (no deposit policy, no
  // configured lines) could switch the link on, save a number, get a green
  // toast — and the storefront rendered nothing, with no way to tell why. Any
  // one of the three keeps the panel.
  if (rows.length === 0 && lines.length === 0 && !contact) return null;

  return (
    // Guide `.dsh > .dd` — 18px side margins, 18px bottom, 16px above, and NO
    // top rule: the disclosure bar it opens from already draws that line.
    <div className={styles.panel}>
      {/* Guide `.dd-g` — three columns of STACKED cells, micro-label over
          value, no rules between them. The label/value rows this replaced ran
          label-left value-right with a border under each: with values of very
          different lengths the numbers landed at six different x positions,
          and the rules cut the panel into stripes. Stacked, every label starts
          on the same left edge and every value sits directly under its own. */}
      {rows.length > 0 ? (
        <dl className={styles.facts}>
          {rows.map((row) => (
            <div key={row.label} className={styles.fact}>
              <dt className={styles.factLabel}>{row.label}</dt>
              <dd className={styles.factValue}>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {lines.length > 0 ? (
        <ul
          {...tourPageSurfaceProps("departure-details")}
          className={styles.lines}
        >
          {lines.map((line) => (
            <li key={line.text} className={styles.line}>
              <span className={styles.glyphBox}>
                <DetailGlyph path={line.iconPath} />
              </span>
              {line.text}
            </li>
          ))}
        </ul>
      ) : null}

      <AskOnWhatsappLine
        extras={extras}
        formattedDate={formatDate(startIso)}
        // The rule separates this line from the facts and lines above it. On a
        // panel where the link is the ONLY content, there is nothing to
        // separate it from and the border reads as a stray line across the
        // card.
        divided={rows.length > 0 || lines.length > 0}
        t={t}
      />
    </div>
  );
}

/** Guide `.dd-f`. Only the WhatsApp half of it: the guide's footer also asserts
 *  "permits are filed in Kathmandu before you fly", which is copy about one
 *  specific tour and not a fact we hold a field for.
 *
 *  Renders nothing unless the operator switched THIS surface on and saved a
 *  number — an "Ask on WhatsApp" line that links nowhere is worse than no line.
 *  That decision is `buildTourPageView`'s; the absence of `whatsappContact` is
 *  the whole test here. */
function AskOnWhatsappLine({
  extras,
  formattedDate,
  divided,
  t,
}: {
  extras?: DepartureExtras | undefined;
  /** This row's date, in the agency's locale — what `{date}` becomes. */
  formattedDate: string;
  /** Draw the rule above. False when this line is the panel's only content,
   *  where a divider separates it from nothing. */
  divided: boolean;
  t: Translator;
}) {
  const contact = extras?.whatsappContact;
  if (!contact || !extras) return null;

  return (
    <p
      {...tourPageSurfaceProps("whatsapp")}
      className={styles.ask}
      data-divided={divided ? "" : undefined}
    >
      {contact.promptLabel || t("Not sure this date works?")}{" "}
      <a
        href={departureChatUrl(contact, extras, formattedDate)}
        target="_blank"
        rel="noopener"
        // The deep success ink, not the discount FILL used as ink — the fill
        // falls under the floor on this panel, and this is a 14px semibold
        // link. See the note on the package card's "You Save" line.
        className={styles.askLink}
      >
        <WhatsappMark className={styles.askMark} />
        {contact.linkLabel || t("Ask on WhatsApp")}
      </a>
    </p>
  );
}

/** Which fact rows this departure actually has, in the guide's order.
 *
 *  THE single source of truth for the omission rules — `buildFactRows` below
 *  renders exactly these keys, and `hasDepartureDetails` counts them to decide
 *  whether the disclosure button appears at all. Kept separate from the
 *  rendering because deciding IF a row exists needs no formatters, and the
 *  button has to make that call before the panel is ever built.
 *
 *  Two copies of these conditions would drift, and the failure is silent: a
 *  "See more details" button that opens onto an empty box. */
export function factRowKeys(facts: DepartureFacts): FactRowKey[] {
  const keys: FactRowKey[] = [];
  // `> 0`, not `!= null` — see the note in `departure-row.tsx`. A zero deposit
  // is a pay-later policy, not a missing one, and a "Deposit today: $0" row
  // states it as if it were a charge.
  if (facts.depositPercent != null && facts.depositPercent > 0) {
    keys.push("deposit");
  }
  if (facts.balanceDueDaysBefore != null) keys.push("balance");
  return keys;
}

type FactRowKey = "deposit" | "balance";

/** Whether the disclosure panel has anything at all to show.
 *
 *  False means `departure-row.tsx` renders no "See more details" button: an
 *  operator who has configured no departure details and set no payment policy
 *  would otherwise get a button that expands onto nothing.
 *
 *  Counts all three of the panel's sources — the derived facts, the operator's
 *  own lines from Tours → Configuration → Tour Page, and the WhatsApp contact
 *  line at its foot. */
export function hasDepartureDetails({
  facts,
  extras,
}: {
  facts: DepartureFacts;
  extras?: DepartureExtras | undefined;
}): boolean {
  return (
    factRowKeys(facts).length > 0 ||
    (extras?.lines.length ?? 0) > 0 ||
    extras?.whatsappContact != null
  );
}

/** The panel's rows, in the guide's order, skipping every fact the operator
 *  has not set. Separate from the JSX because the omission rules are the
 *  interesting part of this component and the markup is four lines. */
function buildFactRows({
  priceAmount,
  startIso,
  facts,
  formatMoney,
  formatDate,
  t,
}: {
  priceAmount: number;
  startIso: string;
  facts: DepartureFacts;
  formatMoney: (amountMinor: number) => string;
  formatDate: (iso: string) => string;
  t: Translator;
}): { label: string; value: string }[] {
  // Every omission rule lives in `factRowKeys` — this only renders what it
  // says is there, so the button's "is there anything?" answer and the panel's
  // contents cannot disagree.
  return factRowKeys(facts).map((key) => {
    switch (key) {
      case "deposit":
        return {
          label: t("Deposit today"),
          value: t("{amount} per traveler", {
            amount: formatMoney(
              // The `!` is held up by `factRowKeys` above, which emits this key
              // ONLY inside `depositPercent != null && > 0` (line ~289). The
              // guard and the read now sit in different functions, so say what
              // enforces it — and note what breaking it would look like: `null`
              // here makes the product `NaN`, and `formatMoney` returns "" for
              // a non-finite amount, so the row would read "Deposit today: per
              // traveler" with the number simply missing rather than throwing.
              // `departure-details-empty.test.ts` pins the key's absence for
              // both `null` and `0`.
              Math.round((priceAmount * facts.depositPercent!) / 100),
            ),
          }),
        };
      case "balance":
        return {
          label: t("Balance due"),
          value: formatDate(shiftDays(startIso, -facts.balanceDueDaysBefore!)),
        };
    }
  });
}

/** Shift a `YYYY-MM-DD` by N days and return the same format.
 *
 *  Built from the string's own parts rather than `new Date(iso)`, which parses
 *  as UTC midnight and then reports LOCAL calendar fields — so west of UTC
 *  every result lands a day early. A balance-due date that is wrong by one day
 *  in one hemisphere is exactly the sort of quiet error nobody reports. */
function shiftDays(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y!, m! - 1, d! + days);
  return localDateStr(date);
}
